import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { AppState } from "../../../../RootReducer";
import { getVisibleCurrencies, getLoggedIn } from "../../../../selectors/user-selectors";
import { 
  getCurrencyDefinitions, 
  getPreferredFiatExchange, 
  showFiatValue 
} from "../../../../selectors/settings-selectors";
import { getContainer } from "../../../../ioc/IOCSetup";

interface BlinkLauncherProps extends React.HTMLAttributes<HTMLElement> {
  game: string;
  token: string | null;
  currency: string | null;
  lang: string;
  theme: string;
  currencyData: {
    showFiat: boolean;
    currencyDefinitions: any;
    preferredExchangeCurrency: string;
  };
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'blink-launcher': React.DetailedHTMLProps<BlinkLauncherProps, HTMLElement>;
    }
  }
}

interface BlinkCasinoGameProps {
  launchUrl: string;
  fullscreen?: boolean;
}

interface LaunchConfig {
  game?: string;
  token?: string | null;
  brand?: string;
  url?: string;
}

const DEFAULT_BLINK_URL = "https://d1835mas0gwu6p.cloudfront.net/scripts/prod/blink-launcher.umd.js";

export const BlinkCasinoGameComponent: React.FC<BlinkCasinoGameProps> = ({ 
  launchUrl, 
  fullscreen = false 
}) => {
  const isMounted = useRef(true);
  const blinkLauncherRef = useRef<HTMLElement | null>(null);
  const loggedIn = useSelector(getLoggedIn);

  // Parse launch configuration
  const parsedLaunchConfig = useMemo<LaunchConfig>(() => {
    try {
      return JSON.parse(launchUrl) || {};
    } catch {
      return {};
    }
  }, [launchUrl]);

  // State management
  const [gameConfig, setGameConfig] = useState({
    game: parsedLaunchConfig.game || "limbo",
    token: loggedIn ? parsedLaunchConfig.token ?? null : null,
    lang: getContainer().getSettingsService().getActiveLocale().code || "en",
    theme: parsedLaunchConfig.brand || "neobet",
    blinkUrl: parsedLaunchConfig.url || DEFAULT_BLINK_URL
  });

  const [scriptState, setScriptState] = useState({
    loaded: false,
    initializing: true,
    error: null as Error | null
  });

  // Redux selectors
  const currencyData = useSelector((state: AppState) => ({
    showFiat: showFiatValue(state),
    currencyDefinitions: getCurrencyDefinitions(state),
    preferredExchangeCurrency: getPreferredFiatExchange(state)
  }));

  const currency = localStorage.getItem("lastSelectedCurrency");

  // Handle React and ReactDOM globals
  useEffect(() => {
    if (!(window as any).React) (window as any).React = React;
    if (!(window as any).ReactDOM) (window as any).ReactDOM = ReactDOM;

    return () => {
      delete (window as any).React;
      delete (window as any).ReactDOM;
    };
  }, []);

  // Handle script loading
  useEffect(() => {
    const script = document.createElement("script");
    script.src = gameConfig.blinkUrl;
    script.async = true;

    const handleLoad = () => {
      if (isMounted.current) {
        setScriptState(prev => ({
          ...prev,
          loaded: true,
          initializing: false
        }));
        const chatButtonWrapper = document.querySelector('.chat-button-wrapper') as HTMLElement;
        if (chatButtonWrapper) chatButtonWrapper.style.display = 'none';
      }
    };

    const handleError = (error: any) => {
      if (isMounted.current) {
        setScriptState(prev => ({
          ...prev,
          loaded: false,
          initializing: false,
          error: error instanceof Error ? error : new Error('Script load failed')
        }));
      }
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
    document.body.appendChild(script);

    return () => {
      isMounted.current = false;
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);

      const chatButtonWrapper = document.querySelector('.chat-button-wrapper') as HTMLElement;
      if (chatButtonWrapper) chatButtonWrapper.style.display = 'inherit';

      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [gameConfig.blinkUrl]);

  // Handle token updates

  if (scriptState.initializing || !gameConfig || !currency || !currencyData) {
    return (
      <div id="jsLoader" className="js-loader">
        <img src="/preloader.png" id="site-loader-blink-2" width="64" alt="Loading" />
      </div>
    );
  }

  if (scriptState.error) {
    return (
      <div className="error-container">
        <p>Failed to load game. Please try again later.</p>
        <p>{scriptState.error.message}</p>
      </div>
    );
  }

  return (
    <div 
      className={`blink-game-container ${scriptState.initializing ? 'initialising' : ''}`} 
      id="gameHeadWrapper"
    >
      <blink-launcher
        ref={blinkLauncherRef}
        game={gameConfig.game}
        token={gameConfig.token}
        currency={currency}
        lang={gameConfig.lang}
        theme={gameConfig.theme}
        currencyData={currencyData}
      />
    </div>
  );
};

export default BlinkCasinoGameComponent;